import SecureClient from '../api/secure-client';
import debug from '../debug';
import DataLakeTracker from '../tracking/data-lake-tracker';
import { setCertificate } from '../../redux/features/certificateSlice';
import { parseCertificate, isCertificateUsable } from './certificate';
import { setForceEncryption } from '../constants';

/**
 * Fetches the server's public certificate and puts it in redux.
 *
 * This is the bootstrap half of the envelope scheme; `./certificate.js` is the
 * read half. It runs once per page load (see `ensureCertificate`), NOT once per
 * API call — the certificate lives in the redux `certificate` slice, which
 * redux-persist keeps in localStorage, so it is already available on a reload
 * or a client-side route change.
 *
 * NOTE ON IMPORTS: this module imports `SecureClient`, so it must never be
 * re-exported from `./index.js`. `SecureClient` imports that barrel, and adding
 * this file to it would create an import cycle.
 */

/** The certificate endpoint. Always uses the LEGACY scheme — see below. */
export const GET_CERTIFICATE_ROUTE =
  'customeronboarding/getcertificatebyorigin';

/** Fixed payload value for every getcertificate call, same as `getkeys`. */
export const GET_CERTIFICATE_VALUE = 'AHEX5IKyI7tHqhTSxCLe';

// Shared promise so concurrent callers trigger only one network request.
let inFlight = null;
// Failed attempts are retried, but only a few times. Without this, a backend
// that is down would get one fresh getcertificate attempt per API call, since
// every envelope request asks for the certificate it is missing.
let attempts = 0;
const MAX_FETCH_ATTEMPTS = 3;

const getReduxStore = () =>
  (typeof window !== 'undefined' && window.__REDUX_STORE__) || null;

const reportFailure = (reason) => {
  debug('ERROR', `[SECURE] getcertificate failed: ${reason}`);

  // No certificate means the envelope scheme is impossible, so force it off:
  // calls waiting on this fetch (and every later one this page load) go out on
  // the legacy scheme instead of being blocked. Set before reporting, so the
  // datalake event below is sent the same way.
  setForceEncryption(true);

  try {
    DataLakeTracker({
      logEventType: 'error',
      eventName: 'GET_CERTIFICATE_FAILED',
      eventValue: String(reason),
    });
  } catch (err) {
    console.log('Failed to report getcertificate failure: ', err);
  }
};

/**
 * Calls `getcertificate` and stores the result in redux.
 *
 * The call itself is encrypted with the LEGACY customerId scheme (with a null
 * customerId, i.e. the default key). It has to be: this is the call that gets
 * us the certificate, so the envelope scheme is not yet possible. The route is
 * also listed in `LEGACY_ONLY_ROUTES` in `utils/api/secure-client.js`, so it
 * stays on the legacy scheme even if that flag is on.
 *
 * @returns {Promise<boolean>} true when a usable certificate was stored
 */
export const fetchCertificate = () =>
  new Promise((resolve) => {
    SecureClient({
      isEncrypted: true,
      useLegacyEncryption: true,
      customerId: null,
      otherPayloadDetailsPresent: true,
      route: GET_CERTIFICATE_ROUTE,
      // isExternalBaseUrl :true,
      // externalBaseUrl : 'https://portal-qa.fibe.in/customeronboarding/getcertificatebyorigin',
      payload: { value: GET_CERTIFICATE_VALUE },
      successFn: (res) => {
        const certificate = res?.body?.certificate;

        if (!certificate) {
          reportFailure(
            `no certificate in response (statusCode=${res?.statusCode})`
          );
          return resolve(false);
        }

        // Validate before committing: a certificate we cannot parse is worse
        // than none at all, because it would silently disable the envelope
        // scheme for the whole session.
        try {
          parseCertificate(certificate);
        } catch (err) {
          reportFailure(`unparseable certificate: ${err && err.message}`);
          return resolve(false);
        }

        const store = getReduxStore();
        if (!store) {
          reportFailure('redux store unavailable');
          return resolve(false);
        }

        // A usable certificate is back, so hand control back to the env flag.
        setForceEncryption(false);
        store.dispatch(
          setCertificate({
            certificate,
            certificateVersion: res?.body?.certificateVersion,
          })
        );

        debug(
          'WATCH',
          `[SECURE] certificate stored (version ${res?.body?.certificateVersion})`
        );
        return resolve(true);
      },
      errorFn: (err) => {
        reportFailure(err && err.message ? err.message : err);
        resolve(false);
      },
    });
  });

/**
 * Fetches the certificate once per page load.
 *
 * Called from `CertificateBootstrap` on mount, and from `SecureClient` when a
 * request needs a certificate that is not there yet.
 *
 * A certificate still held in redux is reused as-is. Since redux-persist keeps
 * it in localStorage, a refresh or hard refresh makes NO network call at all.
 * Only a first visit, cleared storage, or an expired certificate triggers a
 * fetch.
 *
 * Concurrent callers share one request, so a page that fires ten API calls at
 * once triggers a single getcertificate.
 *
 * @returns {Promise<boolean>} true when a usable certificate is available
 */
export const ensureCertificate = () => {
  if (isCertificateUsable()) return Promise.resolve(true);
  if (inFlight) return inFlight;
  if (attempts >= MAX_FETCH_ATTEMPTS) return Promise.resolve(false);
  console.log('this code is running');

  attempts += 1;
  inFlight = fetchCertificate().then((ok) => {
    inFlight = null;
    return ok;
  });

  return inFlight;
};

/**
 * Forces a fetch even when a usable certificate is already cached. Use this if
 * the server rotates its certificate before the cached one expires — nothing
 * calls it automatically today.
 */
export const refreshCertificate = () => {
  attempts = 0;
  inFlight = fetchCertificate().then((ok) => {
    inFlight = null;
    return ok;
  });
  return inFlight;
};
